// Componente PoliticasEASI
// Página de políticas contra la Explotación y el Abuso Sexual Infantil

import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import Button from '../../components/UI/Button';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';

/**
 * Componente PoliticasEasi
 * 
 * Muestra las políticas de Amarsi contra la explotación y abuso sexual infantil
 * Accessible tanto para usuarios autenticados como públicos
 */
const PoliticasEasi = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
                <FiArrowLeft className="h-5 w-5 mr-2" />
                Volver al inicio
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <AmarsiLogo width='2rem' height='2rem'/>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Amarsi</h1>
            </div>
            <nav className="flex items-center space-x-4">
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Privacidad
              </Link>
              <Link 
                to="/terminos-compras" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Términos de Compra
              </Link>
              <Link to="/login">
                <Button variant="primary" size="sm">
                  Iniciar Sesión
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Contenido principal */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Política contra la Explotación y el Abuso Sexual Infantil (EASI)
            </h1>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              En <strong>Amarsi</strong>, estamos firmemente comprometidos con la protección de los niños y adolescentes. 
              Nuestra aplicación se adhiere estrictamente a las siguientes políticas para prevenir y combatir la explotación 
              y el abuso sexual infantil:
            </p>
          </div>

          <div className="space-y-8 text-gray-700 dark:text-gray-300">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Definiciones</h2>
              <p className="mb-4">De acuerdo con las directrices de Google Play y organizaciones internacionales:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Explotación Sexual Infantil:</strong> Uso de un menor en actividades sexuales con el fin de obtener 
                  beneficios económicos, sociales o políticos. Esto incluye la producción, distribución y consumo de material 
                  de abuso sexual infantil.
                </li>
                <li>
                  <strong>Abuso Sexual Infantil:</strong> Participación de un menor en actividades sexuales que no comprende 
                  completamente, para las cuales no está preparado o no puede dar su consentimiento informado, o que violan 
                  las leyes y tabúes sociales.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Tolerancia Cero</h2>
              <p className="mb-4">Prohibimos categóricamente cualquier contenido o conducta que implique la explotación o el abuso sexual infantil, incluyendo, pero no limitado a:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Material de abuso sexual infantil (CSAM).</li>
                <li>Grooming o acoso en línea dirigido a menores.</li>
                <li>Trata de personas con fines de explotación sexual.</li>
                <li>Sextorsión: Amenazas o chantajes a menores mediante el acceso real o presunto a imágenes íntimas del menor.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Medidas Preventivas</h2>
              <p className="mb-4">Implementamos las siguientes medidas para garantizar un entorno seguro:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Moderación de Contenido:</strong> Utilizamos herramientas avanzadas para detectar y eliminar cualquier 
                  contenido inapropiado relacionado con menores.
                </li>
                <li>
                  <strong>Verificación de Usuarios:</strong> Requerimos que los usuarios proporcionen información precisa y 
                  verificable para minimizar riesgos.
                </li>
                <li>
                  <strong>Educación y Concienciación:</strong> Proporcionamos recursos educativos para que los usuarios reconozcan 
                  y reporten conductas inapropiadas.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. Mecanismos de Denuncia</h2>
              <p className="mb-4">Facilitamos a nuestros usuarios herramientas para reportar cualquier contenido o comportamiento sospechoso:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Botón de Reporte:</strong> Cada perfil y publicación cuenta con una opción para reportar abusos.
                </li>
                <li>
                  <strong>Atención al Cliente:</strong> Los usuarios pueden contactarnos directamente a través de 
                  <a href="mailto:help@clubamarsi.com" className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 ml-1">
                    help@clubamarsi.com
                  </a> 
                  {' '}para reportar incidentes.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. Colaboración con Autoridades</h2>
              <p>Nos comprometemos a:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Reportar inmediatamente cualquier actividad ilegal relacionada con la explotación o el abuso sexual infantil a las autoridades competentes.</li>
                <li>Cooperar plenamente con las investigaciones oficiales, proporcionando toda la información necesaria.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">6. Cumplimiento Legal</h2>
              <p>
                Nuestra aplicación cumple con todas las leyes y regulaciones internacionales relacionadas con la protección infantil, 
                incluyendo las políticas establecidas por Google Play.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">7. Actualizaciones de la Política</h2>
              <p>
                Revisamos y actualizamos periódicamente nuestra política de EASI para garantizar su eficacia y cumplimiento 
                con las normativas vigentes.
              </p>
            </section>

            <section className="bg-violet-50 dark:bg-violet-900/20 p-6 rounded-lg border border-violet-200 dark:border-violet-700">
              <p className="text-violet-800 dark:text-violet-200">
                Si tienes preguntas o deseas reportar un incidente, por favor, contacta con nosotros a través de{' '}
                <a href="mailto:help@clubamarsi.com" className="font-semibold underline">
                  help@clubamarsi.com
                </a>.
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-neutral-800 mt-4 border-t border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <AmarsiLogo width='2rem' height='2rem'/>
              <span className="text-gray-900 dark:text-white font-semibold">Amarsi</span>
              <span className="text-gray-500">© {new Date().getFullYear()} Amarsi. Todos los derechos reservados.</span>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-white font-medium"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Privacidad
              </Link>
              <Link 
                to="/terminos-compras" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Términos de Compra
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PoliticasEasi;
